import { apiUrl, orgReady } from '../../api/client';
import { readSse, type SseMessage } from '../../api/stream';
import { currentIdToken, dropSession } from '../../auth/userManager';
import { orgHeaders } from '../../api/orgSelection';
import { backoffMs, parseEnvelope, retryFloor, type LiveEvent, type StreamStatus } from './liveModel';

const IDLE_MS = 45_000; // 3 missed 15 s heartbeats → reconnect

export interface RunnerHandlers {
  onStatus: (status: StreamStatus) => void;
  onError: (message: string | null) => void;
  onEvent: (event: LiveEvent) => void;
}

/**
 * One subscription to GET /v1/stream?topics=… with the bearer header (fetch + ReadableStream): resumes with
 * Last-Event-ID, backs off on failure and reconnects when heartbeats stop. `start` begins, `dispose` ends it.
 */
export class LiveStreamRunner {
  private stopped = false;
  private attempt: AbortController | null = null;
  private idle: ReturnType<typeof setTimeout> | undefined;
  private lastId: string | null = null;
  private retryMs = 3000;
  private failures = 0;
  private readonly stop = new AbortController();

  constructor(
    private readonly topics: string,
    private readonly on: RunnerHandlers,
  ) {}

  start(): void {
    void this.loop();
  }

  dispose(): void {
    this.stopped = true;
    this.stop.abort();
    this.attempt?.abort();
    clearTimeout(this.idle);
  }

  private armIdle = (): void => {
    clearTimeout(this.idle);
    this.idle = setTimeout(() => this.attempt?.abort(new Error('no heartbeat')), IDLE_MS);
  };

  private wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms);
      this.stop.signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true },
      );
    });
  }

  private async loop(): Promise<void> {
    while (!this.stopped) {
      this.attempt = new AbortController();
      this.on.onStatus(this.failures === 0 ? 'connecting' : 'reconnecting');
      if ((await this.connectOnce(this.attempt.signal)) === 'quit') return;
      clearTimeout(this.idle);
      if (this.stopped) return;
      this.failures += 1;
      this.on.onStatus('reconnecting');
      await this.wait(backoffMs(this.retryMs, this.failures, Math.random()));
    }
  }

  /** `quit` when the session is gone (sign-in expired or rejected: no retry), `retry` otherwise. */
  private async connectOnce(signal: AbortSignal): Promise<'quit' | 'retry'> {
    try {
      const body = await this.open(signal);
      if (!body) return 'quit';
      this.failures = 0;
      this.on.onStatus('connected');
      this.on.onError(null);
      this.armIdle();
      await readSse(body, { onChunk: this.armIdle, onRetry: this.onRetry, onMessage: this.onMessage }, signal);
      // The server closed the stream (deploy, timeout): reconnect after the advertised retry.
    } catch (e) {
      if (!this.stopped) this.on.onError(e instanceof Error ? e.message : String(e));
    }
    return 'retry';
  }

  /** The response body, or `null` after dropping the session (no token, or the API answered 401). */
  private async open(signal: AbortSignal): Promise<ReadableStream<Uint8Array> | null> {
    const token = await currentIdToken();
    if (!token) {
      void dropSession('expired');
      return null;
    }
    await orgReady(token);
    const res = await fetch(apiUrl('/stream', { topics: this.topics }), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream', ...(this.lastId ? { 'Last-Event-ID': this.lastId } : {}), ...orgHeaders() },
      cache: 'no-store',
      signal,
    });
    if (res.status === 401) {
      void dropSession('unauthorized');
      return null;
    }
    if (!res.ok || !res.body) throw new Error(`stream answered ${res.status}`);
    return res.body;
  }

  private onRetry = (ms: number): void => {
    this.retryMs = retryFloor(ms);
  };

  private onMessage = (m: SseMessage): void => {
    if (m.id) this.lastId = m.id;
    if (m.event === 'reset') {
      this.lastId = null;
      return;
    }
    const ev = parseEnvelope(m.data);
    if (ev) this.on.onEvent(ev);
  };
}
